<script setup lang="ts">
import { useDebounce } from '@vueuse/core'
import { LucideSearch, LucideNewspaper, LucideCircleX } from 'lucide-vue-next'
import { FeaturePagination } from '~/components/feature/pagination'
import type { SearchResponse } from 'meilisearch'
import {
  EntityExchangeOfficeItem,
  EntityExchangeOfficeItemSkeleton
} from '~/components/entity/exchange-office'
import type { IExchangeOffice, IExchangeOfficeQueryParams } from '~/components/entity/exchange-office'

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const { locale } = useI18n()

const hitsPerPage = Number(route.query.per_page || 10)
const searchQueryString = ref(String(route.query.q || ''))
const currentPage = ref(Number(route.query.page) || 1)

const debouncedQuery = useDebounce(searchQueryString, 500)

// Compute search key based on all filter dependencies
const searchKey = computed(() =>
  ['exchange-offices-search', debouncedQuery.value, currentPage.value].join('-')
)

// Use useAsyncData for SSR support
const {
  data: result,
  pending: isSearching,
  error: searchError
} = await useAsyncData<SearchResponse<IExchangeOffice> | null>(
  searchKey,
  async () => {
    const { search } = useMeiliSearch(`${config.public.meiliEnvironment}_exchange_offices`)

    try {
      return (await search(debouncedQuery.value, {
        hitsPerPage,
        sort: ['weight:desc', 'highlight:desc', 'updated_at:desc'],
        page: currentPage.value,
        attributesToHighlight: ['title'],
        attributesToSearchOn: [
          'title.ky',
          'title.ru',
          'description.ky',
          'description.ru',
          'license',
          'okpo'
        ]
      })) as unknown as SearchResponse<IExchangeOffice>
    } catch (e) {
      console.error('MeiliSearch fetch error:', e)
      return null
    }
  },
  {
    server: true,
    lazy: true,
    watch: false,
    default: () => null
  }
)

const hasError = computed(() => !!searchError.value)

const typedHits = computed(() => {
  return (result.value?.hits || []).map((hit) => ({
    id: hit.id,
    title: hit._formatted?.title[locale.value] || hit.title[locale.value],
    logo: hit.logo,
    head_branch_location: hit.head_branch_location,
    license: hit.license,
    okpo: hit.okpo,
    mobile_phone: hit.mobile_phone,
    highlight: hit.highlight
  }))
})

// Initialize component
const isInitializing = ref(true)

onMounted(() => {
  isInitializing.value = false
})

const props = withDefaults(
  defineProps<{
    queryParams?: IExchangeOfficeQueryParams
    hideSearch?: boolean
  }>(),
  {
    queryParams: () => ({
      per_page: '10',
      q: ''
    }),
    hideSearch: false
  }
)

watch(
  () => props.queryParams,
  () => {
    if (isInitializing.value) return
    currentPage.value = 1
    searchQueryString.value = props.queryParams?.q || ''
  },
  { deep: true }
)

watch(debouncedQuery, () => {
  if (isInitializing.value) return
  currentPage.value = 1
  router.replace({
    query: {
      ...route.query,
      q: debouncedQuery.value,
      page: undefined
    }
  })
})

const changePage = (page: number) => {
  router.replace({
    query: {
      ...route.query,
      q: searchQueryString.value,
      page
    }
  })
  currentPage.value = page
}
</script>

<template>
  <div class="space-y-5">
    <div v-if="!hideSearch" class="relative">
      <Input
        v-model="searchQueryString"
        :placeholder="$t('common.search_placeholder')"
        class="h-[52px] pl-14 pr-14"
      />
      <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />
      <Button
        v-if="searchQueryString"
        size="icon"
        variant="link"
        class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
        @click="searchQueryString = ''"
      >
        <LucideCircleX />
      </Button>
    </div>

    <section v-if="!isSearching && !hasError && result" aria-labelledby="exchange-offices">
      <div v-if="result.hits?.length" class="CardsList">
        <Heading id="exchange-offices" tag="h2" class="sr-only">{{
          $t('common.exchange_offices')
        }}</Heading>
        <template v-for="item in typedHits" :key="item.id">
          <EntityExchangeOfficeItem :item="item" :hide-organization-details="true" />
        </template>

        <div class="mt-7 flex justify-end">
          <FeaturePagination
            :meta="{
              current_page: result.page || 1,
              total: result.totalHits || 0,
              per_page: result.hitsPerPage || 0,
              last_page: result.totalPages || 0
            }"
            :links="{
              prev:
                currentPage > 1
                  ? `?q=${searchQueryString}&page=${result.page ? result.page - 1 : 1}`
                  : null,
              next:
                currentPage < (result.totalPages || 1)
                  ? `?q=${searchQueryString}&page=${result.page ? result.page + 1 : 1}`
                  : null
            }"
            @change="changePage"
          />
        </div>
      </div>

      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{ $t('common.nothing_found') }}</AlertDescription>
      </Alert>
    </section>

    <section v-else-if="isSearching">
      <EntityExchangeOfficeItemSkeleton :count="hitsPerPage" :hide-organization-details="false" />
    </section>

    <section v-else-if="hasError">
      <Alert variant="yellow">
        <AlertTitle>{{ $t('common.error_loading_title') }}</AlertTitle>
        <AlertDescription>{{ $t('common.error_loading_description') }}</AlertDescription>
      </Alert>
    </section>
  </div>
</template>
